import { medusaClient } from '@lib/config'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import Button from '@modules/common/components/button'
import Input from '@modules/common/components/input'
import { LOCAL_EMAIL_RESET_PASSWORD } from '@constants/local-storage'
import { useRouter } from 'next/router'
import RouterUrl from '@constants/router.constant'
import {
  MIN_LENGTH_PASSWORD,
  MIN_LENGTH_PASSWORD_MESSAGE
} from '@constants/auth.constant'
import AuthForm from '../auth-form'
import { handleAuthError } from '@lib/util/handle-error'
import { useAlert } from '@common/hooks'
import { Text } from '@chakra-ui/react'

interface ResetPasswordForm {
  password: string
  confirm_password: string
}

const ResetPasswordToken: React.FC<{ token: string; emailUser?: string }> = ({
  token,
  emailUser
}) => {
  const [authError, setAuthError] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)

  const router = useRouter()
  const { successToast } = useAlert()

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ResetPasswordForm>()

  useEffect(() => {
    if (errors.password != null || errors.confirm_password != null) {
      setAuthError(
        errors.password?.message ?? errors.confirm_password?.message ?? ''
      )
    }
  }, [errors])

  const onResendMail = async () => {
    setAuthError(undefined)
    setResending(true)
    medusaClient.customers
      .generatePasswordToken({ email: emailUser as string })
      .then(() => {
        setResending(false)
        localStorage.setItem(LOCAL_EMAIL_RESET_PASSWORD, emailUser as string)
        successToast({
          title: 'Sent mail success',
          description: 'Please check your email to process reset password'
        })
      })
      .catch((e) => {
        if (e?.response?.status == 404) {
          setAuthError('Email not found or not exist')
          setResending(false)
          return
        }
        handleAuthError(e, (mess) => {
          setAuthError(mess)
        })
        setResending(false)
      })
  }

  const onSubmit = handleSubmit(async ({ password, confirm_password }) => {
    setAuthError(undefined)
    setLoading(true)

    if (password !== confirm_password) {
      setAuthError('Confirm password not match with password')
      setLoading(false)
      return
    }

    const email =
      emailUser ?? localStorage.getItem(LOCAL_EMAIL_RESET_PASSWORD) ?? ''

    medusaClient.customers
      .resetPassword({ email, token, password })
      .then(() => {
        setLoading(false)
        localStorage.removeItem(LOCAL_EMAIL_RESET_PASSWORD)
        router.replace(RouterUrl.AUTH.LOGIN)
      })
      .catch((e) => {
        setAuthError('Invalid or expired password reset token')
        setLoading(false)
      })
  })

  return (
    <AuthForm onSubmit={onSubmit}>
      <div className="flex flex-col w-full gap-y-5">
        <Input
          label="Password"
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: MIN_LENGTH_PASSWORD,
              message: MIN_LENGTH_PASSWORD_MESSAGE
            }
          })}
          type="password"
          autoComplete="current-password"
          disabled={loading}
        />
        <Input
          label="Confirm Password"
          {...register('confirm_password', {
            required: 'Confirm Password is required'
          })}
          type="password"
          autoComplete="current-password"
          disabled={loading}
        />
      </div>
      {authError != null && (
        <div>
          <span className="w-full text-rose-500 text-small-regular">
            {authError}
          </span>
        </div>
      )}
      {emailUser != null && authError != null && (
        <Text fontSize={'18px'}>
          <button
            onClick={() => {
              onResendMail()
            }}
            className="underline"
            type="button"
          >
            Resend password reset instructions
          </button>
        </Text>
      )}
      <Button
        className="mt-6"
        isLoading={loading}
        disabled={loading || resending}
      >
        Reset Password
      </Button>
    </AuthForm>
  )
}

export default ResetPasswordToken
